package dev.lingvo.site.components

import dev.lingvo.shared.PlanStatus
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.i18n.tr

/**
 * What a subscription's state is called, written once for every surface that reports it.
 *
 * Two surfaces described the same six states in two different ways. `/app/settings/billing` printed
 * `status.name.lowercase().replace('_', ' ')`, which reaches every reader as "past due" and
 * "canceling" whatever language they chose, and is one rename of a wire constant away from
 * rewriting the sentence a customer reads about their own subscription. The project settings Plan
 * tab had written the words out properly but kept them `private`, so the fix could not travel.
 *
 * It lives in `components/` rather than in `:kit` because a plan status is a Lingvo billing noun,
 * not a design-system one, and because both callers are `:site` pages — one under
 * `pages/app/settings/`, one under `pages/app/projects/[slug]/settings/` — with no closer home in
 * common. `tr`, not `t`: the badge on the billing page builds its label as a plain argument, and
 * the Plan tab writes the same word into an `LStatDelta` outside composition.
 */
fun PlanStatus.statusWords(): String = when (this) {
    PlanStatus.ACTIVE -> tr("plan_words.active", "Active")
    PlanStatus.TRIALING -> tr("plan_words.in_trial", "In trial")
    PlanStatus.PAST_DUE -> tr("plan_words.payment_retrying", "Payment retrying")
    PlanStatus.CANCELING -> tr("plan_words.ending", "Ending")
    PlanStatus.CANCELED -> tr("plan_words.canceled", "Canceled")
    PlanStatus.PAUSED -> tr("plan_words.paused", "Paused")
}

/**
 * The badge tone for a status, kept beside the words so the two cannot drift apart.
 *
 * `CANCELED` is `NEUTRAL`, not `SUCCESS`. The billing page's own `else -> SUCCESS` branch painted a
 * subscription that has already ended in the same green as one that is running, which is a semantic
 * colour attached to something other than the state it names — the one thing the palette rule
 * forbids. It is a fact, not a fault: nothing is retrying, nothing is owed.
 */
fun PlanStatus.statusBadgeVariant(): LBadgeVariant = when (this) {
    PlanStatus.ACTIVE, PlanStatus.TRIALING -> LBadgeVariant.SUCCESS
    PlanStatus.PAST_DUE -> LBadgeVariant.DANGER
    PlanStatus.CANCELING, PlanStatus.PAUSED -> LBadgeVariant.WARNING
    PlanStatus.CANCELED -> LBadgeVariant.NEUTRAL
}
